import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  Mail,
  MapPin,
  Shield,
  Clock,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Apple,
  Play,
  CheckCircle,
  MessageCircle,
  FileText
} from 'lucide-react';

const CabBookingFooter = () => {
  const currentYear = new Date().getFullYear();

  // Ride Options
  const rideOptions = [
    { name: 'Local City Cabs', href: '/services' },
    { name: 'Airport Taxi Transfer', href: '/services' },
    { name: 'Outstation Cab Booking', href: '/services' },
    { name: 'Hourly Car Rental', href: '/services' },
    { name: 'Nava Kailayam Temple Tour', href: '/booking' },
    { name: 'Vehicle Fleet Options', href: '/vehicle' },
  ];

  // SEO & Directories
  const seoDirectories = [
    { name: 'Latest Travel Blogs (2026)', href: '/blogs' },
    { name: '1000+ Tirunelveli Cab Keywords', href: '/tirunelveli-cab-service' },
    { name: 'All Area SEO Service Pages', href: '/seo-pages' },
    { name: 'Nava Kailayam Taxi Circuit', href: '/taxi-services-in-nava-kailayam-temples' },
    { name: 'Tirunelveli Railway Taxi Pickup', href: '/tirunelveli-railway-station-taxi-cab-pickup' },
    { name: 'Hotel Pickup & Drop Cabs', href: '/hotel-pickup-drop-cab-services-tirunelveli' },
  ];

  // Company Links
  const companyLinks = [
    { name: 'About Aadvika Cabs', href: '/contactus' },
    { name: 'Our Cab Services', href: '/services' },
    { name: 'Customer FAQ & Help', href: '/faq' },
    { name: 'Travel & Cab Blogs', href: '/blogs' },
    { name: 'Contact & 24/7 Booking', href: '/contactus' },
  ];

  // Contact Info
  const contactInfo = {
    phones: ['+91 86103 41454', '+91 82203 75973'],
    email: 'aselvapadmanabhan@gmail.com',
    address: 'No-4, Second Floor, Rajendra Nagar, Palayamkottai, Tirunelveli 627002'
  };

  // Developer Credit Component
  const DeveloperCredit = () => (
    <div className="bg-black py-4 border-t border-gray-800">
      <div className="container mx-auto px-4 text-center">
        <a
          href="https://freelancerx.in"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 text-gray-500 hover:text-cyan-400 transition-all duration-300 group"
        >
          <span className="text-xs">
            Developed by <span className="font-semibold text-gray-400 group-hover:text-cyan-400">FreelancerX</span>
          </span>
          <img 
            src="https://freelancerx.in/static/media/footerlogo.e53de59ccd9ef1553485.png" 
            alt="FreelancerX"
            className="h-7 w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity rounded-md"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.alt = 'FreelancerX';
            }}
          />
        </a>
      </div>
    </div>
  );

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Brand & Contact */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div>
                <h2 className="text-white text-2xl font-bold">Aadvika<span className="text-cyan-400">Cabs</span></h2>
                <p className="text-gray-400 text-xs">24/7 Tirunelveli Cab Booking Service</p>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm mb-5 leading-relaxed">
              Tirunelveli's trusted taxi and cab service provider offering 24/7 local city rides, outstation packages, airport transfers, and spiritual temple tours.
            </p>

            <div className="space-y-2 text-xs text-gray-300 mb-6">
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-cyan-400 flex-shrink-0" />
                <a href="tel:+918610341454" className="hover:text-white transition">+91 86103 41454</a> / 
                <a href="tel:+918220375973" className="hover:text-white transition">+91 82203 75973</a>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-cyan-400 flex-shrink-0" />
                <a href="mailto:aselvapadmanabhan@gmail.com" className="hover:text-white transition">aselvapadmanabhan@gmail.com</a>
              </div>
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-cyan-400 flex-shrink-0 mt-0.5" />
                <span>{contactInfo.address}</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-3">
              <a href="https://wa.me/918610341454" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-500 text-white p-2 rounded-full transition">
                <MessageCircle size={16} />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white p-2 rounded-full transition">
                <Facebook size={16} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white p-2 rounded-full transition">
                <Instagram size={16} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white p-2 rounded-full transition">
                <Twitter size={16} />
              </a>
            </div>
          </div>

          {/* Ride Options */}
          <div>
            <h3 className="text-white font-semibold text-base mb-4 border-l-2 border-cyan-400 pl-2">Ride Options</h3>
            <ul className="space-y-2">
              {rideOptions.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <CheckCircle className="w-3 h-3 mr-2 text-cyan-400 opacity-60 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* SEO & Blogs */}
          <div>
            <h3 className="text-white font-semibold text-base mb-4 border-l-2 border-cyan-400 pl-2">Blogs & SEO Directory</h3>
            <ul className="space-y-2">
              {seoDirectories.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-cyan-300 transition-colors text-sm flex items-center group"
                  >
                    <FileText className="w-3 h-3 mr-2 text-cyan-400 opacity-60 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links & Company */}
          <div>
            <h3 className="text-white font-semibold text-base mb-4 border-l-2 border-cyan-400 pl-2">Company & Help</h3>
            <ul className="space-y-2 mb-6">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            <div className="bg-gray-800/80 p-4 rounded-xl border border-gray-700">
              <h4 className="text-white font-bold text-xs uppercase mb-1 text-cyan-400">24/7 Fast Booking</h4>
              <p className="text-gray-300 text-xs mb-3">Instant confirmation via call or WhatsApp.</p>
              <a 
                href="tel:+918610341454"
                className="w-full inline-flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold py-2 rounded-lg transition"
              >
                <Phone size={12} /> Call +91 86103 41454
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="bg-gray-950 py-4 border-t border-gray-800 text-xs text-gray-500">
        <div className="container mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-2 text-center sm:text-left">
          <p>© {currentYear} Aadvika Cabs. All rights reserved. Tirunelveli, Tamil Nadu, India.</p>
          <div className="flex gap-4">
            <Link to="/tirunelveli-cab-service" className="hover:text-gray-300">SEO Keywords Index</Link>
            <Link to="/blogs" className="hover:text-gray-300">Blogs</Link>
            <Link to="/faq" className="hover:text-gray-300">FAQ</Link>
            <Link to="/contactus" className="hover:text-gray-300">Contact</Link>
          </div>
        </div>
      </div>

      {/* Developer Credit */}
      <DeveloperCredit />
    </footer>
  );
};

export default CabBookingFooter;
